import { useState, useCallback, useEffect } from 'react';
import { useTracking, PAYMENT_EVENTS } from './useTracking';
import { GEM_PACKAGES, SUBSCRIPTION_PLANS } from '@/lib/ehPrices';
import type { GemPackage, SubscriptionPlan } from '@/lib/ehPrices';

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        'iap-products-request'?: {
          postMessage: (skus: string[]) => void;
        };
        'iap-purchase-request'?: {
          postMessage: (jsonString: string) => void;
        };
        'iap-manage-subscriptions-request'?: {
          postMessage: (_: string) => void;
        };
        'storefront-country-request'?: {
          postMessage: (_: string) => void;
        };
      };
    };
  }
}

/** From Swift wrapper */
export type SwiftPurchaseState = 'success' | 'notFound' | 'canceled' | 'failed';

export interface AppleProductDetails {
  attributes:{
    description:{
      standard: string;
    },
    icuLocale: string; // e.g. "en_US@currency=USD"
    isFamilyShareable: 0 | 1;
    kind: string; // e.g. "Consumable"
    name: string;
    offerName: string;
    offers:[
      {
        currencyCode: string;
        price: string;
        priceFormatted: string; // "$0.99"
      }
    ]
  },
  href: string;
  id: string;
}

export interface AppleTransactionDetails  {
  // userId that we passed in Swift wrapper to attribute the transaction server-side
  appAccountToken?: string;
  originalPurchaseDate: number;
  deviceVerificationNonce: string;
  subscriptionGroupIdentifier: string;
  environment: string; // e.g. "Xcode"
  inAppOwnershipType: string; // e.g. "PURCHASED"
  originalTransactionId: string;
  transactionReason: string; // e.g. "PURCHASE"
  storefront: string; // e.g. "USA"
  signedDate: number;
  bundleId: string; // app bundle ID
  storefrontId: string; // e.g. "143441"
  transactionId: string; // e.g. "10"
  type: string; // e.g. "Auto-Renewable Subscription"
  webOrderLineItemId: string; // e.g. "0"
  isUpgraded: boolean;
  productId: string;
  deviceVerification: string;
  purchaseDate: number;
  expiresDate: number;
  quantity: number;
}

export const useAppleStoreKit = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [products, setProducts] = useState<AppleProductDetails[]>([]);
  const [storefrontCountry, setStorefrontCountry] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useTracking();

  const initialize = useCallback(() => {
    if (window.webkit?.messageHandlers && 'iap-products-request' in window.webkit.messageHandlers) {
      setIsSupported(true);
      if (import.meta.env.DEV) console.log('Apple StoreKit is supported');

      if ('storefront-country-request' in window.webkit.messageHandlers) {
        window.addEventListener('storefront-country-result', (e) => {
          const customEvent = e as CustomEvent;
          if (customEvent.detail) {
            setStorefrontCountry(customEvent.detail);
          }
        }, { once: true });
        window.webkit?.messageHandlers?.['storefront-country-request']?.postMessage('');
      }
    } else {
      setIsSupported(false);
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const getProductDetails = useCallback(async (skus: string[]) => {
    if (!isSupported) {
      if (import.meta.env.DEV) console.error('Apple StoreKit not initialized.');
      return;
    }

    const handleProductsResult = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        try {
          const productDetails = JSON.parse(customEvent.detail) as AppleProductDetails[];
          setProducts(productDetails);
        } catch (e) {
          setError('Failed to parse product details.');
          trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
            paymentMethod: 'appleStore',
            error: `Failed to parse product details: ${e instanceof Error ? e.message : String(e)}`,
          });
        }
      }
      window.removeEventListener('iap-products-result', handleProductsResult);
    };

    window.addEventListener('iap-products-result', handleProductsResult, { once: true });
    window.webkit?.messageHandlers?.['iap-products-request']?.postMessage(skus);
  }, [isSupported, trackEvent]);
  
  const purchase = useCallback(async ({ onResult, sku, userId }: { onResult: (state: SwiftPurchaseState) => void, sku: string, userId?: string }) => {
    if (!isSupported || !userId) {
      if (import.meta.env.DEV) console.error('Apple StoreKit not initialized or user not logged in.');
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'appleStore',
        error: 'Apple StoreKit not initialized or user not logged in.',
      });
      return { success: false };
    }

    const transactionDetails = {
      paymentMethod: 'appleStore',
      billedAmount: 0,
      plan: undefined as SubscriptionPlan | undefined,
      gemPackage: undefined as GemPackage | undefined
    }

    const gemPackage = GEM_PACKAGES.find(pkg => pkg.appleStoreSku === sku);
    const plan = SUBSCRIPTION_PLANS.find(p => p.appleStoreSku === sku);

    if (gemPackage) {
      transactionDetails.gemPackage = gemPackage;
      transactionDetails.billedAmount = gemPackage.price;
    } else if (plan) {
      transactionDetails.plan = plan;
      transactionDetails.billedAmount = plan.billedAmount;
    } else {
      setError(`Product not found`);
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        ...transactionDetails,
        error: `Unknown product ID: ${sku}`,
      });
      return { success: false };
    }

    const handlePurchaseTransaction = async (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const transactionPayload = JSON.parse(customEvent.detail);
        if (transactionPayload) {
          // For Apple, we don't verify client-side. The transaction will be verified
          // via Apple Server Notifications webhook.
          // We just show pending success dialog to the user in view.
          return { success: true };
        } else { // this should never happen, as iap-purchase-transaction is only messaged on success
          setError(`Purchase failed.`);
          trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
            ...transactionDetails,
            error: `Missing transaction payload`,
          });
          return { success: false };
        }
      }
    };

    const handlePurchaseResult = (event: Event, onResult: (state: SwiftPurchaseState) => void) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const state = customEvent.detail as SwiftPurchaseState;
        onResult(state);
        if (state === 'success') {
          trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
            ...transactionDetails,
            sku,
          });
        } else if (state === 'failed' || state === 'notFound') {
          trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
            ...transactionDetails,
            error: `Swift state: ${state}`,
          });
        } else if (state === 'canceled') {
          trackEvent(PAYMENT_EVENTS.PAYMENT_CANCELED, {
            ...transactionDetails,
            sku,
          });
        }
      }
    };

    window.addEventListener('iap-purchase-transaction', handlePurchaseTransaction, { once: true });
    window.addEventListener('iap-purchase-result', (e) => handlePurchaseResult(e, onResult), { once: true });

    window.webkit?.messageHandlers?.['iap-purchase-request']?.postMessage(JSON.stringify({
      productId: sku,
      quantity: 1,
      userId,
    }));
    
    // This function will not return the result directly, but the event listener will handle it.
    // We can return a promise that resolves when the event is handled, but for now, this is simpler.
    return { success: true, pending: true };
  }, [isSupported, trackEvent]);

  const manageSubscriptions = useCallback(async () => {
    if (!isSupported) {
      if (import.meta.env.DEV) console.error('Apple StoreKit not initialized.');
      return;
    }

    window.webkit?.messageHandlers?.['iap-manage-subscriptions-request']?.postMessage('');

    return { success: true, pending: true };
  }, [isSupported]);

  return { isSupported, products, getProductDetails, purchase, error, storefrontCountry, manageSubscriptions };
}; 